// src/store/slices/filterSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';

// --- Types ---
export interface Filter {
  zone_name: string | null;
  filter_name: string;
  filter_target: string;
  filter_data: any;
  description?: string | null;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface FilterTarget {
  target: string;
  description?: string | null;
}

interface RejectValue {
  message?: string;
  error?: string;
}

interface FiltersState {
  items: Filter[];
  selected: Filter | null;
  targets: FilterTarget[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: FiltersState = {
  items: [],
  selected: null,
  targets: [],
  status: 'idle',
  error: null,
};

// Helpers
const zonePath = (zone: string | null | undefined) => (zone === null || zone === 'null' ? 'null' : (zone as string));

const sameKey = (a: Filter, zone: string | null, name: string) => (a.zone_name ?? null) === (zone ?? null) && a.filter_name === name;

// --- Thunks ---

// List filters (optionally by zone_name and/or filter_target)
// Use { zone_name: null } to fetch globals.
export const fetchFilters = createAsyncThunk<
  { filters: Filter[] },
  { zone_name?: string | null; filter_target?: string } | undefined,
  { rejectValue: RejectValue }
>('filters/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const query: any = {};
    if (params?.zone_name !== undefined) {
      query.zone_name = params.zone_name === null ? 'null' : params.zone_name;
    }
    if (params?.filter_target) query.filter_target = params.filter_target;

    const res = await api.get('/filters', {
      params: flattenQuery(query),
    });
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { error: err.message });
  }
});

// Get one
export const fetchFilter = createAsyncThunk<Filter, { zone_name: string | null; filter_name: string }, { rejectValue: RejectValue }>(
  'filters/fetchOne',
  async ({ zone_name, filter_name }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/filters/${zonePath(zone_name)}/${filter_name}`);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);

// Create
export const createFilter = createAsyncThunk<
  Filter,
  {
    zone_name?: string | null;
    filter_name: string;
    filter_target: string;
    filter_data: any;
    description?: string | null;
    created_by?: number | null;
  },
  { rejectValue: RejectValue }
>('filters/create', async (body, { rejectWithValue }) => {
  try {
    // zone_name can be omitted or null
    const res = await api.post('/filters', {
      ...body,
      zone_name: body.zone_name === undefined ? undefined : body.zone_name, // allow null
    });
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { error: err.message });
  }
});

// Update (can rename and/or retarget)
export const updateFilter = createAsyncThunk<
  Filter,
  {
    zone_name: string | null;
    filter_name: string;
    data: {
      filter_data?: any;
      new_zone_name?: string | null;
      new_filter_name?: string;
      filter_target?: string;
      description?: string | null;
    };
  },
  { rejectValue: RejectValue }
>('filters/update', async ({ zone_name, filter_name, data }, { rejectWithValue }) => {
  try {
    const payload = { ...data };
    if (payload.new_zone_name === null) payload.new_zone_name = 'null' as any; // server expects "null" in path/body normalization
    const res = await api.put(`/filters/${zonePath(zone_name)}/${filter_name}`, payload);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { error: err.message });
  }
});

// Delete
export const deleteFilter = createAsyncThunk<
  { zone_name: string | null; filter_name: string },
  { zone_name: string | null; filter_name: string },
  { rejectValue: RejectValue }
>('filters/delete', async ({ zone_name, filter_name }, { rejectWithValue }) => {
  try {
    await api.delete(`/filters/${zonePath(zone_name)}/${filter_name}`);
    return { zone_name, filter_name };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { error: err.message });
  }
});

// Fetch allowed targets
export const fetchFilterTargets = createAsyncThunk<{ targets: FilterTarget[] }, void, { rejectValue: RejectValue }>(
  'filters/fetchTargets',
  async (_void, { rejectWithValue }) => {
    try {
      const res = await api.get('/filter_targets');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);

// --- Slice ---

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    clearSelected: (state) => {
      state.selected = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFilters
      .addCase(fetchFilters.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchFilters.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.filters || [];
      })
      .addCase(fetchFilters.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as any)?.msg || (action.payload as any)?.error || action.error.message || 'Failed to fetch filters';
      })

      // fetchFilter
      .addCase(fetchFilter.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.selected = null;
      })
      .addCase(fetchFilter.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selected = action.payload;
        // also update items if present
        const idx = state.items.findIndex((f) => sameKey(f, action.payload.zone_name ?? null, action.payload.filter_name));
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(fetchFilter.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as any)?.msg || (action.payload as any)?.error || action.error.message || 'Failed to fetch filter';
      })

      // createFilter
      .addCase(createFilter.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createFilter.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items.push(action.payload);
        const toastId = 'filter-create-success';
        if (!toast.isActive(toastId)) toast.success('Filter created', { toastId });
      })
      .addCase(createFilter.rejected, (state, action) => {
        state.status = 'failed';
        const msg = (action.payload as any)?.msg || (action.payload as any)?.error || action.error.message || 'Failed to create filter';
        state.error = msg;
        const toastId = 'filter-create-fail';
        if (!toast.isActive(toastId)) toast.error(msg, { toastId });
      })

      // updateFilter
      .addCase(updateFilter.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateFilter.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updated = action.payload;
        // find by old or new key (replace if exists, else push)
        const idx = state.items.findIndex((f) => sameKey(f, updated.zone_name ?? null, updated.filter_name));
        if (idx >= 0) state.items[idx] = updated;
        else state.items.push(updated);
        state.selected = updated;
        const toastId = 'filter-update-success';
        if (!toast.isActive(toastId)) toast.success('Filter updated', { toastId });
      })
      .addCase(updateFilter.rejected, (state, action) => {
        state.status = 'failed';
        const msg = (action.payload as any)?.msg || (action.payload as any)?.error || action.error.message || 'Failed to update filter';
        state.error = msg;
        const toastId = 'filter-update-fail';
        if (!toast.isActive(toastId)) toast.error(msg, { toastId });
      })

      // deleteFilter
      .addCase(deleteFilter.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteFilter.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { zone_name, filter_name } = action.payload;
        state.items = state.items.filter((f) => !sameKey(f, zone_name, filter_name));
        if (state.selected && sameKey(state.selected, zone_name, filter_name)) state.selected = null;
        const toastId = 'filter-delete-success';
        if (!toast.isActive(toastId)) toast.success('Filter deleted', { toastId });
      })
      .addCase(deleteFilter.rejected, (state, action) => {
        state.status = 'failed';
        const msg = (action.payload as any)?.msg || (action.payload as any)?.error || action.error.message || 'Failed to delete filter';
        state.error = msg;
        const toastId = 'filter-delete-fail';
        if (!toast.isActive(toastId)) toast.error(msg, { toastId });
      })

      // fetchFilterTargets
      .addCase(fetchFilterTargets.fulfilled, (state, action) => {
        state.targets = action.payload.targets || [];
      })
      .addCase(fetchFilterTargets.rejected, (state, action) => {
        const msg = (action.payload as any)?.msg || (action.payload as any)?.error || action.error.message || 'Failed to fetch filter targets';
        state.error = msg;
      });
  },
});

export const { clearSelected } = filtersSlice.actions;
export default filtersSlice.reducer;
